import { forwardRef } from "react";
import { useTokens } from "@/design-system/hooks/useTokens";
import { InputProps } from "./types";

/**
 * 通用輸入框組件
 * 使用設計 Token 系統來確保視覺一致性
 */
export const Input = forwardRef<HTMLInputElement, InputProps>(
  (
    {
      label,
      helperText,
      error = false,
      size = "medium",
      variant = "outlined",
      fullWidth = false,
      startAdornment,
      endAdornment,
      className = "",
      disabled,
      ...rest
    },
    ref,
  ) => {
    const tokens = useTokens();

    // 根據尺寸設定輸入框樣式
    const getSizeStyles = () => {
      switch (size) {
        case "small":
          return {
            padding: `${tokens.spacing.xs} ${tokens.spacing.sm}`,
            fontSize: tokens.typography.size.sm,
            height: "32px",
          };
        case "large":
          return {
            padding: `${tokens.spacing.sm} ${tokens.spacing.md}`,
            fontSize: tokens.typography.size.lg,
            height: "48px",
          };
        default: // medium
          return {
            padding: `${tokens.spacing.sm} ${tokens.spacing.md}`,
            fontSize: tokens.typography.size.md,
            height: "40px",
          };
      }
    };

    // 根據變體設定輸入框樣式
    const getVariantStyles = () => {
      switch (variant) {
        case "filled":
          return {
            backgroundColor: tokens.colors.gray[100],
            border: "none",
            borderBottom: `2px solid ${error ? tokens.colors.error.main : tokens.colors.gray[300]}`,
            borderRadius: `${tokens.radius.md} ${tokens.radius.md} 0 0`,
            focusBorderColor: error
              ? tokens.colors.error.main
              : tokens.colors.primary.main,
            hoverBgColor: tokens.colors.gray[200],
          };
        case "standard":
          return {
            backgroundColor: "transparent",
            border: "none",
            borderBottom: `1px solid ${error ? tokens.colors.error.main : tokens.colors.gray[300]}`,
            borderRadius: "0",
            focusBorderColor: error
              ? tokens.colors.error.main
              : tokens.colors.primary.main,
            hoverBgColor: "transparent",
          };
        default: // outlined
          return {
            backgroundColor: "transparent",
            border: `1px solid ${error ? tokens.colors.error.main : tokens.colors.gray[300]}`,
            borderRadius: tokens.radius.md,
            focusBorderColor: error
              ? tokens.colors.error.main
              : tokens.colors.primary.main,
            hoverBgColor: "transparent",
          };
      }
    };

    const sizeStyles = getSizeStyles();
    const variantStyles = getVariantStyles();

    // 建立輸入框樣式
    const inputStyle = {
      display: "block",
      width: fullWidth ? "100%" : "auto",
      color: error ? tokens.colors.error.main : tokens.colors.text.primary,
      outline: "none",
      transition: tokens.transitions.normal,
      ...sizeStyles,
      ...variantStyles,
      ...(disabled && {
        backgroundColor: tokens.colors.gray[100],
        color: tokens.colors.text.disabled,
        cursor: "not-allowed",
      }),
    };

    // 將樣式轉換為 Tailwind 類名
    const getTailwindClasses = () => {
      let classes = `
      block
      ${fullWidth ? "w-full" : "w-auto"}
      ${disabled ? "bg-gray-100 text-gray-400 cursor-not-allowed" : ""}
      transition-all
      focus:outline-none
    `;

      // 根據尺寸添加類
      if (size === "small") {
        classes += " text-sm px-2 py-1 h-8";
      } else if (size === "large") {
        classes += " text-lg px-4 py-2 h-12";
      } else {
        classes += " text-base px-4 py-2 h-10";
      }

      // 根據變體添加類
      if (variant === "filled") {
        classes += error
          ? " bg-gray-100 border-b-2 border-error-500 rounded-t-md focus:border-error-500"
          : " bg-gray-100 border-b-2 border-gray-300 rounded-t-md focus:border-primary-500 hover:bg-gray-200";
      } else if (variant === "standard") {
        classes += error
          ? " bg-transparent border-b border-error-500 rounded-none focus:border-error-500"
          : " bg-transparent border-b border-gray-300 rounded-none focus:border-primary-500";
      } else {
        // outlined
        classes += error
          ? " bg-transparent border border-error-500 rounded focus:border-error-500"
          : " bg-transparent border border-gray-300 rounded focus:border-primary-500";
      }

      return classes;
    };

    return (
      <div className={`flex flex-col ${fullWidth ? "w-full" : ""}`}>
        {label && (
          <label
            className={`block mb-1 text-sm font-medium ${error ? "text-error-500" : "text-gray-700 dark:text-gray-300"}`}
            style={{
              marginBottom: tokens.spacing.xs,
              fontSize: tokens.typography.size.sm,
              fontWeight: tokens.typography.weight.medium,
              color: error
                ? tokens.colors.error.main
                : tokens.colors.text.primary,
            }}
          >
            {label}
          </label>
        )}

        <div className="relative flex items-center">
          {startAdornment && (
            <div className="absolute left-2 flex items-center pointer-events-none">
              {startAdornment}
            </div>
          )}

          <input
            ref={ref}
            className={`${getTailwindClasses()} ${startAdornment ? "pl-8" : ""} ${endAdornment ? "pr-8" : ""} ${className}`}
            style={{
              ...inputStyle,
              paddingLeft: startAdornment ? "2rem" : inputStyle.padding,
              paddingRight: endAdornment ? "2rem" : inputStyle.padding,
            }}
            disabled={disabled}
            {...rest}
          />

          {endAdornment && (
            <div className="absolute right-2 flex items-center">
              {endAdornment}
            </div>
          )}
        </div>

        {helperText && (
          <p
            className={`mt-1 text-xs ${error ? "text-error-500" : "text-gray-500 dark:text-gray-400"}`}
            style={{
              marginTop: tokens.spacing.xs,
              fontSize: tokens.typography.size.xs,
              color: error
                ? tokens.colors.error.main
                : tokens.colors.text.secondary,
            }}
          >
            {helperText}
          </p>
        )}
      </div>
    );
  },
);

Input.displayName = "Input";
